import React from "react";
import clsx, { type ClassValue, } from "clsx";
import { twMerge, } from "tailwind-merge";

// Função utilitária que você já utiliza
function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Tipagem baseada na do MUI, simplificada para o uso prático
export interface LinkProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  /**
   * Controla o comportamento do sublinhado.
   * @default 'always'
   */
  underline?: "none" | "hover" | "always";
  /**
   * Cor do link. Pode ser ajustado conforme o seu design system.
   * @default 'primary'
   */
  color?: "primary" | "secondary" | "inherit" | "textPrimary" | "textSecondary";
  /**
   * O componente usado para o nó raiz. Permite renderizar um Link do Next.js ou React Router.
   * @default 'a'
   */
  component?: React.ElementType;
}

const Link = React.forwardRef<HTMLAnchorElement, LinkProps>((props, ref) => {
  const {
    className,
    underline = "always",
    color = "primary",
    component: Component = "a",
    children,
    ...other
  } = props;

  // Dicionário de classes para o sublinhado
  const underlineClasses = {
    none: "no-underline",
    hover: "no-underline hover:underline underline-offset-4",
    always: "underline underline-offset-4",
  };

  // Dicionário de classes para as cores
  const colorClasses = {
    primary: "text-blue-600 hover:text-blue-700", // Substitua por text-primary se configurado no seu tailwind.config
    secondary: "text-purple-600 hover:text-purple-700",
    inherit: "text-inherit",
    textPrimary: "text-slate-900",
    textSecondary: "text-slate-500",
  };

  return (
    <Component
      ref={ref}
      className={cn(
        // Estilos base e acessibilidade (Focus)
        "inline-flex items-baseline outline-none transition-colors duration-200 cursor-pointer",
        "focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500 rounded-sm",
        // Variantes
        underlineClasses[underline],
        colorClasses[color],
        className
      )}
      {...other}
    >
      {children}
    </Component>
  );
});

Link.displayName = "Link";

export default Link;